import { apiFetch } from '@/api/client'

/**
 * Lab `Game/getGames` row. `_id` is the Lab Mongo Game id that
 * `Account/getDownlineAccountsData` `game` still expects.
 */
export type GetGamesRow = {
  _id: string
  name: string
  rules?: string
}

export type GetGamesResponse = {
  value: boolean
  data: GetGamesRow[]
}

/** Lab PL Statement hid these table-game names from the product dropdown. */
const HIDDEN_GAME_NAMES = new Set(['TeenPatti', 'Poker', 'Roulette'])

/** Demo / fixture catalog — same Lab ids as Agent-API `GAME_CATALOG`. */
export const FIXTURE_GAMES: GetGamesRow[] = [
  { _id: '5b36053e46123555cca93993', name: 'SportsBook', rules: '' },
  { _id: '60f5637b64b34916022db498', name: 'Premium Sportsbook', rules: '' },
  { _id: '5a818572650cb46dd9b1a25a', name: 'Live Casino', rules: '' },
  { _id: '67a1c2a5e6081920a1b2c3d4', name: 'Crash Game', rules: '' },
  { _id: '5f311f236eae8b1e0f35e59a', name: 'Card Game', rules: '' },
  { _id: '60ed93b341bae473d53ae139', name: 'Racing', rules: '' },
]

/** Normalize the Lab callback and drop the names PL Statement never listed. */
export function rowsFromGetGames(response: GetGamesResponse): GetGamesRow[] {
  if (!response.value) return []
  return (response.data ?? []).filter(
    (row) => Boolean(row._id && row.name) && !HIDDEN_GAME_NAMES.has(row.name),
  )
}

/** Product picker — Agent-API hierarchy `POST /v1/game/getGames`. */
export async function getGames(): Promise<GetGamesRow[]> {
  const response = await apiFetch<GetGamesResponse>('/v1/game/getGames', {
    method: 'POST',
    body: JSON.stringify({}),
  })
  return rowsFromGetGames(response)
}

export type GetSubGamesResponse = {
  value: boolean
  data: string[]
}

/** Same Lab `Game.subGame` strings as Agent-API `subGamesForGame`. */
const SPORTSBOOK_SUBGAMES = ['Cricket', 'Soccer', 'Tennis'] as const
const RACING_SUBGAMES = ['Horse Racing', 'Greyhound Racing'] as const
const LIVE_CASINO_SUBGAMES = [
  'Live ROULETTE',
  'Live BLACKJACK',
  'Live CASINO HOLD',
  'Live BACCARAT',
  'Live ULTIMATE TEXAS HOLDEM',
  'Live EXTREME TEXAS HOLDEM',
  'Live THREE CARD POKER',
  'Live TRIPLE CARD POKER',
  'Live CARIBBEAN STUD',
  'Live MONEY WHEEL',
] as const
const CARD_SUBGAMES = ['teenpatti', 'poker', 'andarbahar'] as const
const CRASH_SUBGAMES = ['Casino'] as const

const SUBGAMES_BY_ID: Record<string, readonly string[]> = {
  '5b36053e46123555cca93993': SPORTSBOOK_SUBGAMES,
  '60f5637b64b34916022db498': SPORTSBOOK_SUBGAMES,
  '5a818572650cb46dd9b1a25a': LIVE_CASINO_SUBGAMES,
  '67a1c2a5e6081920a1b2c3d4': CRASH_SUBGAMES,
  '5f311f236eae8b1e0f35e59a': CARD_SUBGAMES,
  '60ed93b341bae473d53ae139': RACING_SUBGAMES,
}

const SUBGAMES_BY_NAME: Record<string, readonly string[]> = {
  sportsbook: SPORTSBOOK_SUBGAMES,
  'premium sportsbook': SPORTSBOOK_SUBGAMES,
  'live casino': LIVE_CASINO_SUBGAMES,
  'crash game': CRASH_SUBGAMES,
  'card game': CARD_SUBGAMES,
  racing: RACING_SUBGAMES,
}

/**
 * Demo catalog for `Account/getSubGames`. Missing / `all` → SportsBook (Lab).
 */
export function fixtureSubGames(game?: string | null): string[] {
  const key = (game ?? '').trim()
  if (!key || key.toLowerCase() === 'all') return [...SPORTSBOOK_SUBGAMES]
  return [...(SUBGAMES_BY_ID[key] ?? SUBGAMES_BY_NAME[key.toLowerCase()] ?? [])]
}

/** Lab callback `{ value, data: string[] }` — names for the Sub Game picker. */
export function namesFromGetSubGames(response: GetSubGamesResponse): string[] {
  if (!response.value) return []
  return (response.data ?? []).filter(
    (name): name is string => typeof name === 'string' && Boolean(name.trim()),
  )
}

/** Sport / table picker — Agent-API hierarchy `POST /v1/account/getSubGames`. */
export async function getSubGames(game?: string | null): Promise<string[]> {
  const body = game && game !== 'all' ? { game } : { game: 'all' }
  const response = await apiFetch<GetSubGamesResponse>('/v1/account/getSubGames', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return namesFromGetSubGames(response)
}
